import React, { useEffect, useRef, useState, useId } from 'react';
import { Link } from 'react-router-dom';
import AppButton from 'components/atoms/AppButton/AppButton';

interface Props {
  title?: string
  options: {
    title: string
    path?: string
    icon?: string
    onClick?: () => void
  }[]
  color: 'primary' | 'ghost' | 'dark' | 'default' | 'secondary' | 'info'
  size?: 'sm' | 'md' | 'lg'
  iconRight?: React.ReactNode
  imageSrc?: string
  imageClassName?: string
}

export const ButtonDropdown = ({
  title,
  options,
  color,
  size = 'md',
  iconRight,
  imageSrc,
  imageClassName = 'cursor-pointer',
}: Props) => {
  const [showOptions, setShowOptions] = useState(false)
  const buttonDropdownRef = useRef<HTMLDivElement>(null)
  const selfId = useId();

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!showOptions) return;
      if (buttonDropdownRef.current && !buttonDropdownRef.current.contains(e.target as Node)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptions]);

  // Auto-cerrar otros dropdowns al abrir éste (y viceversa)
  useEffect(() => {
    const onSomeoneOpened = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string }>).detail;
      if (!detail) return;
      if (detail.id !== selfId && showOptions) setShowOptions(false);
    };
    window.addEventListener('app:dropdown-open', onSomeoneOpened as EventListener);
    return () => window.removeEventListener('app:dropdown-open', onSomeoneOpened as EventListener);
  }, [showOptions, selfId]);

  const toggle = () => {
    const next = !showOptions;
    setShowOptions(!showOptions);
    if (next) {
      window.dispatchEvent(new CustomEvent('app:dropdown-open', { detail: { id: selfId } }));
    }
  };

  return (
    <div ref={buttonDropdownRef} className="relative inline-block">
      {typeof imageSrc === 'string' && imageSrc.trim() !== '' ? (
        <img
          src={imageSrc}
          className={imageClassName}
          onClick={(e) => {
            e.stopPropagation()
            toggle()
          }}
        />
      ) : (
        <AppButton
          state={color}
          title={title ?? ''}
          size={size}
          iconRight={
            iconRight ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  verticalAlign: 'middle',
                  width: 16,
                  height: 16,
                  transform: showOptions ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                {iconRight}
              </span>
            ) : undefined
          }
          onClick={() => { toggle() }}
        />
      )}

      {showOptions && (
        <div className="z-10 bg-white shadow-lg flex flex-col rounded-md absolute mt-1 w-fit min-w-[130px]">
          {options.map(({ path, title, icon, onClick }, index) => {
            const content = (
              <div className="flex items-center">
                {typeof icon === 'string' && icon.trim() !== '' && (
                  <img src={icon} className="img-logo w-[16px] h-[16px] mr-2" alt="Icon" />
                )}
                {title}
              </div>
            )

            return path ? (
              <Link
                to={path}
                className="p-2 hover:bg-gray-100 whitespace-nowrap"
                key={index}
                onClick={(e) => {
                  e.stopPropagation()
                  setShowOptions(false)
                }}
              >
                {content}
              </Link>
            ) : (
              <div
                className="p-2 hover:bg-gray-100 whitespace-nowrap cursor-pointer"
                key={index}
                onClick={(e) => {
                  e.stopPropagation()
                  onClick?.()
                  setShowOptions(false)
                }}
              >
                {content}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
